import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { contentFor, type Currency, type Locale, type Product } from "@nh/shared";
import { cn } from "@/shared/lib/cn";
import { numberLocaleOf, priceFor, type ResolvedPrice } from "../shop.catalog";
import { usePriceViewer } from "../usePriceViewer";
import { ProductCardCta } from "./ProductCardCta";
import { ProductCardMedia } from "./ProductCardMedia";
import { ProductCardPrice } from "./ProductCardPrice";
import { ProductCardTitle } from "./ProductCardTitle";
import { ProductPill } from "./ProductPill";

export interface ProductCardProps {
  product: Product;
  /** Moneda activa de la tienda. */
  currency: Currency;
  /** Idioma del CONTENIDO del producto (viene del servidor, no de i18n). */
  locale: Locale;
  /**
   * Ficha del producto (`/producto/:slug`). Sin `to`, la tarjeta se pinta
   * sin enlace — así sigue sirviendo en la galería del design system y en
   * cualquier listado que no lleve a ninguna parte, sin un enlace muerto.
   */
  to?: string;
  /**
   * La TIENDA PERSONALIZADA reusa esta misma tarjeta (Anahí, 27-ago:
   * "estos productos deben ser igual a /shop"): su precio ya resuelto
   * en lugar del de catálogo, SU stock en la píldora, y el botón en su
   * color de marca. Sin estas props, la tarjeta es la de la casa.
   */
  priceOverride?: ResolvedPrice | null;
  unitsOverride?: number;
  accent?: string;
}

/**
 * La tarjeta del catálogo.
 *
 * ── Es la tarjeta de itsthebest1111.com ──────────────────────────────
 * Misma anatomía, y la lista importa porque es lo que hace que las dos
 * tiendas se reconozcan como la misma: cápsula de 28 px, foto en 3:4 que
 * se acerca a 1.08 en 900 ms, dos píldoras de cristal arriba (categoría a
 * la izquierda, unidades a la derecha), y abajo —SOBRE la foto, no debajo
 * de ella— el rótulo, el nombre a dos líneas, el precio en versalita fina
 * y el botón "VER PRODUCTO" que sube al despertar la tarjeta.
 *
 * Lo que NO se copió va anotado donde ocurre: el velo y el fondo de las
 * píldoras, que en producción dejan texto blanco por debajo del mínimo
 * legible sobre una fotografía clara.
 *
 * ── El texto viene traducido del servidor ────────────────────────────
 * `contentFor(product.translations, locale)` — el nombre de un producto lo
 * escribe quien administra el catálogo, no quien programa, así que no
 * vive en los archivos de traducción de la interfaz. Cuando falta el
 * inglés, `contentFor` cae al español solo.
 *
 * ── La primera imagen ────────────────────────────────────────────────
 * `imageUrl` es la principal; `imageUrls[0]` es el respaldo para los
 * productos que solo tienen galería.
 *
 * ── La píldora de la derecha ─────────────────────────────────────────
 * Producción muestra ahí las unidades ("500 UDS"). Aquí igual, con dos
 * matices: un `stock` en cero NO se pinta —"0 UDS" sobre un producto que
 * sigue a la venta confunde más de lo que informa— y, cuando el producto
 * no lleva existencias capturadas, el hueco lo ocupa la presentación
 * ("60 cápsulas · 30 porciones"), que es el otro dato de esa esquina.
 */
export function ProductCard({
  product,
  currency,
  locale,
  to,
  priceOverride,
  unitsOverride,
  accent,
}: ProductCardProps) {
  const { t } = useTranslation();

  const content = contentFor(product.translations, locale);
  const viewer = usePriceViewer();
  const price = priceOverride !== undefined ? priceOverride : priceFor(product, currency, viewer);
  const image = product.imageUrl || product.imageUrls?.[0];

  const stockShown = unitsOverride !== undefined ? unitsOverride : product.stock;
  const units = stockShown !== undefined && stockShown > 0 ? stockShown : null;
  const label = product.category || content.tagline || t("Producto");

  const card = (
    <article
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-ds-xl",
        "border border-ds-border bg-ds-surface shadow-glass-sm",
        "transition-all duration-500 hover:shadow-glass group-hover:shadow-glass",
        "group-focus-within:shadow-glass",
      )}
    >
      <ProductCardMedia src={image} name={content.name} />

      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between gap-2 p-4">
        {product.category ? <ProductPill>{product.category}</ProductPill> : <span />}
        {units !== null ? (
          <ProductPill>{t("{{n}} UDS", { n: units })}</ProductPill>
        ) : (
          product.presentation && <ProductPill>{product.presentation}</ProductPill>
        )}
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 p-5">
        <div className="flex items-end justify-between gap-4">
          <ProductCardTitle label={label} name={content.name} />

          <div className="shrink-0">
            <ProductCardPrice price={price} numberLocale={numberLocaleOf(locale)} />
          </div>
        </div>

        {to && <ProductCardCta accent={accent} />}
      </div>
    </article>
  );

  if (!to) return card;

  /*
   * El `group` va AQUÍ, en el enlace, y no solo en la tarjeta: el botón
   * "VER PRODUCTO" tiene que aparecer también cuando la tarjeta recibe el
   * foco del tabulador, y quien recibe ese foco es este enlace. Con el
   * `.group` una capa más adentro, `group-focus-within` nunca se
   * cumpliría —el elemento enfocado quedaría FUERA del grupo— y la
   * tarjeta seguiría escondiendo su acción a quien navega con teclado.
   */
  return (
    <Link
      to={to}
      className={cn(
        "group block h-full rounded-ds-xl transition-transform duration-300",
        "hover:-translate-y-1.5 motion-reduce:transform-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50 focus-visible:ring-offset-2",
      )}
    >
      {card}
    </Link>
  );
}
